import { useState, useEffect, useRef, SetStateAction } from 'react';
import { Formik } from 'formik';
import {
    IMenuItem,
    Table,
    TableHeadCell,
    TableRow,
    TableCell,
    Button,
    IconButton,
    IconButtonWithText,
    Modal,
    Pagination,
    MixerLoader,
    PremiumTooltip,
} from '../../Common';
import { useNavigate } from 'react-router';
import { useHttpGetInvoicesOnMount } from '../../../hooks';
import { useCanCreate } from '../../../hooks/features';
import { InvoiceDto } from '../../../commonTypes/invoice.types';
import { useTranslation } from '../../../hooks/useTranslation';
import { InvoiceStatus, Feature } from '@cyberlabrs/helpers';
import { useDeleteInvoice } from '../../../hooks/invoice/useHttpDeleteInvoice';
import { formatToFullMonthDayYear } from '../../../util/date/dateFormater';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useResizeListener } from '../../../hooks/windowEvents/useResizeListener';
import NotAvailableOnMobile from '../../Common/NotAvailableOnMobile';
import { ExpandableSearch } from '../../Common/ExpandableSearch';
import Drawer from '../../Common/Drawer';
import EditingInvoiceHistory from '../../EditingInvoiceHistory';
import { DeleteModal } from '../../Common/DeleteModal';
import StatusBadge from '../../StatusBadge';

interface FormValues {
    search: string;
}

interface InvoiceTableProps {
    clientId?: string;
    isClientPage?: boolean;
}

type Sort = 'createdAt' | 'clientName' | 'number';

type Direction = 'asc' | 'desc';

const InvoiceTable = ({ clientId, isClientPage = false }: InvoiceTableProps) => {
    const { t } = useTranslation('invoice');
    const menuItems: IMenuItem[] = [
        { label: t('draft'), value: 'DRAFT' },
        { label: t('ready'), value: 'READY' },
        { label: t('sent'), value: 'SENT' },
        { label: t('overdue'), value: 'OVERDUE' },
        { label: t('paid'), value: 'PAID' },
        { label: t('sendingFailed'), value: 'SENDING_FAILED' },
    ];

    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const [tempFilters, setTempFilters] = useState(selectedFilters);
    const [selectedInvoices, setSelectedInvoices] = useState<InvoiceDto[]>([]);
    const [invoiceIdToDelete, setInvoiceIdToDelete] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isYesNoVisible, setIsYesNoVisible] = useState(false);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState<number>(10);
    const [areFiltersExpanded, setAreFiltersExpanded] = useState(false);
    const [sort, setSort] = useState<Sort>('createdAt');
    const [direction, setDirection] = useState<Direction>('desc');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedAuditDescriptions, setSelectedAuditDescriptions] = useState<any>([]);

    const isMobile = useResizeListener().isMaxWidthLarge;

    const filtersRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLImageElement>(null);

    useClickOutside(filtersRef, () => setAreFiltersExpanded(false), toggleRef);

    const { deleteInvoice } = useDeleteInvoice();

    const {
        data: invoices,
        isLoading,
        total: invoicesTotal,
        refetch,
    } = useHttpGetInvoicesOnMount({
        mode: 'pagination',
        search: searchTerm || undefined,
        statuses: selectedFilters.length > 0 ? (selectedFilters as InvoiceStatus[]) : undefined,
        page,
        size: perPage,
        sort,
        direction,
        clientId,
    });

    const selectAllRef = useRef<HTMLInputElement>(null);
    const allSelected = invoices.length > 0 && selectedInvoices.length === invoices.length;
    const partiallySelected = selectedInvoices.length > 0 && !allSelected;

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = partiallySelected;
        }
    }, [selectedInvoices, partiallySelected]);

    const toggleSelectAll = () => {
        setSelectedInvoices(allSelected ? [] : invoices.map(inv => inv));
    };

    const toggleSingle = (invoice: InvoiceDto) => {
        setSelectedInvoices(prev => (prev.some(i => i.id === invoice.id) ? prev.filter(i => i.id !== invoice.id) : [...prev, invoice]));
    };

    const navigate = useNavigate();

    const handleExportSelected = async (selected: InvoiceDto[]) => {
        for (const [index, inv] of selected.entries()) {
            setTimeout(async () => {
                try {
                    const response = await fetch(inv.fileUrl, { mode: 'cors' });
                    if (!response.ok) throw new Error('Network response was not ok');

                    const arrayBuffer = await response.arrayBuffer();
                    const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `invoice-${inv.number}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    window.URL.revokeObjectURL(url);
                } catch (err) {
                    console.error(`Error downloading invoice ${inv.number}:`, err);
                }
            }, index * 500);
        }
    };

    const handleDeleteClick = (invoiceId: string) => {
        setInvoiceIdToDelete(invoiceId);
        setIsYesNoVisible(true);
    };

    const handleConfirmDelete = () => {
        if (!invoiceIdToDelete) return;

        void deleteInvoice(invoiceIdToDelete, () => {
            refetch();
            setIsYesNoVisible(false);
            setInvoiceIdToDelete(null);
            setSelectedInvoices(prev => prev.filter(inv => inv.id !== invoiceIdToDelete));
        });
    };

    const handleCancelDelete = () => {
        setIsYesNoVisible(false);
        setInvoiceIdToDelete(null);
    };

    const handlePageChange = (newPage: SetStateAction<number>) => {
        setPage(newPage);
    };

    const handleCheckboxChange = (value: string) => {
        setTempFilters(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
    };

    const handleApplyFilters = () => {
        setSelectedFilters(tempFilters);
        setAreFiltersExpanded(false);
    };

    const handleResetFilters = () => {
        setTempFilters([]);
        setSelectedFilters([]);
        setAreFiltersExpanded(false);
    };

    const handleSort = (newSortValue: Sort) => {
        if (newSortValue === sort) {
            setDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setDirection('desc');
        }
        setSort(newSortValue);
    };
    useEffect(() => {
        refetch();
    }, [searchTerm, selectedFilters, perPage, page, sort, direction]);

    // Calculate current month's invoice count for limit checking
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const currentMonthInvoicesCount = invoices.filter(inv => {
        const invDate = new Date(inv.createdAt);
        return invDate.getMonth() === currentMonth && invDate.getFullYear() === currentYear;
    }).length;

    const canCreate = useCanCreate(Feature.INVOICING, 'create', currentMonthInvoicesCount);

    const handleCreateInvoice = () => {
        if (!canCreate.canCreate) return;
        navigate('/invoices/create');
    };

    return isMobile ? (
        <NotAvailableOnMobile
            title={t('notAvailableOnMobileTitle')}
            paragraph={t('notAvailableOnMobileParagraph')}
            buttonText={t('notAvailableOnMobileButton')}
            onClick={() => navigate('/invoices/invoice-list')}
        />
    ) : (
        <>
            <Formik<FormValues> initialValues={{ search: '' }} onSubmit={() => {}}>
                {formik => (
                    <>
                        <div className="invoices__header">
                            <h1 className="invoices__title">{t('invoices')}</h1>
                            <div className="invoices__actions">
                                {!isClientPage && (
                                    <div className="invoices__actions__icons">
                                        <ExpandableSearch
                                            id="search"
                                            placeholder={t('searchInvoice')}
                                            context={formik}
                                            className="invoices__search"
                                            setSearchTerm={setSearchTerm}
                                            isCloseButtonVisible={formik.values.search !== ''}
                                            closeButtonOnClick={() => {
                                                formik.setFieldValue('search', '');
                                                setSearchTerm('');
                                            }}
                                        />
                                        <div className="invoices__actions__icons__filters-container">
                                            <img
                                                ref={toggleRef}
                                                src={areFiltersExpanded ? '/svg/common/filters-blue.svg' : '/svg/common/filters.svg'}
                                                alt="Filter"
                                                onClick={() => setAreFiltersExpanded(prev => !prev)}
                                            />
                                            {areFiltersExpanded && (
                                                <div ref={filtersRef} className="invoices__actions__filters">
                                                    <p className="invoices__actions__filters__title">{t('status')}</p>
                                                    <div className="invoices__actions__filters__options">
                                                        {menuItems.map(item => (
                                                            <label key={item.value} className="invoices__actions__filters__checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={tempFilters.includes(item.value as string)}
                                                                    onChange={() => handleCheckboxChange(item.value as string)}
                                                                />
                                                                <span>{item.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                    <div className="invoices__actions__filters__options__buttons">
                                                        <Button text={t('applyFilters')} onClick={handleApplyFilters} />
                                                        <Button text={t('resetFilters')} variant="text" onClick={handleResetFilters} color="blue" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <img
                                            className={selectedInvoices.length > 0 ? '' : 'invoices__actions__icons--disabled'}
                                            src="/svg/common/download.svg"
                                            alt="Download"
                                            onClick={() => handleExportSelected(selectedInvoices)}
                                        />
                                    </div>
                                )}
                                <div>
                                    {!isClientPage ? (
                                        <div className="premium-tooltip-wrapper">
                                            <Button text={t('addNewInvoice')} onClick={handleCreateInvoice} disabled={!canCreate.canCreate} />
                                            {!canCreate.canCreate && <PremiumTooltip showOnHover position="bottom" />}
                                        </div>
                                    ) : (
                                        <IconButtonWithText
                                            text="Add new invoice"
                                            onClick={handleCreateInvoice}
                                            disabled={!canCreate.canCreate}
                                            iconSrc="/svg/plus-blue-without-circle.svg"
                                            variant="text"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={!isClientPage ? 'invoices' : ''}>
                            <Table className="invoices__table">
                                <TableRow className="invoices__header-cell--background">
                                    <TableHeadCell className="invoices__header-cell--background">
                                        <div className="invoices__header-cell">
                                            {!isClientPage && (
                                                <input
                                                    type="checkbox"
                                                    className="invoice-checkbox"
                                                    ref={selectAllRef}
                                                    checked={allSelected}
                                                    onChange={toggleSelectAll}
                                                />
                                            )}
                                            <div className="invoices__header-cell--sortable">
                                                <span>{t('client')}</span>
                                                <img
                                                    src={sort === 'clientName' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                                    alt="Arrow"
                                                    className={`invoices__header-cell--sortable__arrow
                                                        ${
                                                            direction === 'asc' &&
                                                            sort === 'clientName' &&
                                                            'invoices__header-cell--sortable__arrow--asc'
                                                        }
                                                        `}
                                                    onClick={() => handleSort('clientName')}
                                                />
                                            </div>
                                        </div>
                                    </TableHeadCell>
                                    <TableHeadCell className="invoices__header-cell--background ">
                                        <div className="invoices__header-cell invoices__header-cell--sortable">
                                            {t('invoiceId')}
                                            <img
                                                src={sort === 'number' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                                alt="Arrow"
                                                className={`invoices__header-cell--sortable__arrow 
                                                        ${direction === 'asc' && sort === 'number' && 'invoices__header-cell--sortable__arrow--asc'}
                                                        `}
                                                onClick={() => handleSort('number')}
                                            />
                                        </div>
                                    </TableHeadCell>
                                    <TableHeadCell className="invoices__header-cell--background">
                                        <div className="invoices__header-cell invoices__header-cell--sortable">
                                            {t('date')}
                                            <img
                                                src={sort === 'createdAt' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                                alt="Arrow"
                                                className={`invoices__header-cell--sortable__arrow 
                                                        ${
                                                            direction === 'asc' &&
                                                            sort === 'createdAt' &&
                                                            'invoices__header-cell--sortable__arrow--asc'
                                                        }
                                                        `}
                                                onClick={() => handleSort('createdAt')}
                                            />
                                        </div>
                                    </TableHeadCell>
                                    <TableHeadCell className="invoices__header-cell--background">
                                        <div className="invoices__header-cell">{t('amount')}</div>
                                    </TableHeadCell>
                                    <TableHeadCell className="invoices__header-cell--background">
                                        <div className="invoices__header-cell">{t('status')}</div>
                                    </TableHeadCell>
                                    <TableHeadCell className="invoices__header-cell--background">
                                        <div className="invoices__header-cell">{t('dueDateLabel')}</div>
                                    </TableHeadCell>
                                </TableRow>

                                {invoices.map((invoice, index) => (
                                    <TableRow
                                        key={invoice.number}
                                        onClick={() => navigate(`/invoices/invoice-list`, { state: { invoice } })}
                                        className={`invoices__row invoices__row--hover  ${
                                            index % 2 === 0 ? 'invoices__row--white' : 'invoices__row--gray'
                                        }`}
                                    >
                                        <TableCell className="invoices__cell--background">
                                            <div className="invoices__row-id invoices__cell">
                                                {!isClientPage && (
                                                    <input
                                                        type="checkbox"
                                                        className="invoice-checkbox"
                                                        checked={selectedInvoices.some(inv => inv.id === invoice.id)}
                                                        onChange={() => toggleSingle(invoice)}
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                )}
                                                <span>{invoice.clientName}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="invoices__cell--background">#{invoice.number}</TableCell>
                                        <TableCell className="invoices__cell--background">
                                            <span>{invoice.createdAt ? formatToFullMonthDayYear(invoice.createdAt) : '-'}</span>
                                        </TableCell>
                                        <TableCell className="invoices__cell--background">
                                            {Number(invoice.total ?? 0).toFixed(2)} {invoice.clientCurrency}
                                        </TableCell>
                                        <TableCell className="invoices__cell--background">
                                            <StatusBadge status={invoice.status} />
                                        </TableCell>
                                        <TableCell className="invoices__cell--background">
                                            <div className="invoices__due-date-cell">
                                                <span>{formatToFullMonthDayYear(invoice.dueDate)}</span>

                                                <div className="invoices__due-date-cell__icons">
                                                    {invoice?.auditChanges.length > 0 && (
                                                        <IconButton
                                                            className="invoices__icon-button"
                                                            iconUrl="/svg/common/info.svg"
                                                            variant="icon"
                                                            iconAlt="info"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                setIsDrawerOpen(true);
                                                                setSelectedAuditDescriptions(invoice.auditChanges);
                                                            }}
                                                        />
                                                    )}

                                                    {invoice.status !== InvoiceStatus.SENDING_FAILED && (
                                                        <IconButton
                                                            className="invoices__icon-button"
                                                            iconUrl="/svg/pen-grey.svg"
                                                            variant="icon"
                                                            iconAlt="edit"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                navigate(`/invoices/${invoice.id}/edit`);
                                                            }}
                                                        />
                                                    )}
                                                    <IconButton
                                                        className="invoices__icon-button"
                                                        iconUrl="/svg/trash.svg"
                                                        variant="icon"
                                                        iconAlt="delete"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            handleDeleteClick(invoice.id);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {invoices.length > 0 && !isLoading && (
                                    <TableRow className="invoices__row">
                                        <TableCell colSpan={6}>
                                            <div className="invoices__pagination">
                                                <Pagination
                                                    total={invoicesTotal as number}
                                                    elementsPerPage={perPage}
                                                    setElementsPerPage={setPerPage}
                                                    page={page}
                                                    setPage={page => handlePageChange(page)}
                                                    isClientPage={isClientPage}
                                                />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </Table>

                            {isLoading && <MixerLoader position="centerOnParent" />}
                            {invoices.length === 0 && !isLoading && (
                                <div className="invoices__no-content">
                                    <h3>{t('noInvoices')}</h3>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Formik>
            <Modal isVisible={isYesNoVisible} closeModal={() => setIsYesNoVisible(false)}>
                <DeleteModal
                    className="submit-timesheet__yes-no-modal"
                    title={t('deleteModalTitle')}
                    description={t('deleteModalDescription')}
                    onNo={() => handleCancelDelete()}
                    onYes={() => handleConfirmDelete()}
                />
            </Modal>
            <Drawer
                open={isDrawerOpen}
                title={t('editingInvoiceHistory')}
                onClose={() => setIsDrawerOpen(false)}
                className="invoices__drawer"
                showFooter={true}
                leftButtons={[]}
                rightButtons={
                    [
                        // { text: t('common.cancel'), variant: 'text', onClick: () => setIsDrawerOpen(false) },
                        // { text: t('billing.addCardFormTitle'), variant: 'contained', onClick: handleAddNewCard, disabled: !isAddFormValid },
                    ]
                }
                content={EditingInvoiceHistory(selectedAuditDescriptions)}
            />
        </>
    );
};

export default InvoiceTable;
